import React, { useEffect, useRef, useCallback } from "react";
import LoadingSpinner from "./LoadingSpinner";

/**
 * InfiniteScroll Component
 * 
 * A reusable component that implements infinite scroll functionality.
 * It uses Intersection Observer to detect when the user scrolls near the bottom
 * and triggers the onLoadMore callback.
 * 
 * @param {Function} onLoadMore - Callback function triggered when scrolling near bottom
 * @param {Boolean} hasMore - Whether there are more items to load
 * @param {Boolean} loading - Whether data is currently being loaded
 * @param {React.ReactNode} children - The content to render
 * @param {String|React.Component} loader - Custom loader component (optional)
 * @param {String} loaderClassName - CSS class for loader container (optional)
 * @param {Number} threshold - Threshold for intersection observer (default: 0.1)
 */
const InfiniteScroll = ({
  onLoadMore,
  hasMore,
  loading,
  children,
  loader,
  loaderClassName = "d-flex justify-content-center my-4",
  threshold = 0.1,
}) => {
  const observerRef = useRef(null);
  const loadingRef = useRef(null);

  const handleObserver = useCallback(
    (entries) => {
      const [target] = entries;
      if (target.isIntersecting && hasMore && !loading) {
        onLoadMore();
      }
    },
    [hasMore, loading, onLoadMore]
  );

  useEffect(() => {
    const option = {
      root: null,
      rootMargin: "20px",
      threshold: threshold,
    };

    const observer = new IntersectionObserver(handleObserver, option);
    observerRef.current = observer;

    const target = loadingRef.current;
    if (target) {
      observer.observe(target);
    }

    return () => observer.disconnect();
  }, [handleObserver, threshold, hasMore, loading]);

  // Re-trigger when loading finishes while sentinel is still visible
  useEffect(() => {
    if (loading || !hasMore) return;

    const target = loadingRef.current;
    if (!target) return;

    const rect = target.getBoundingClientRect();
    const isVisible = rect.top < window.innerHeight && rect.bottom > 0;
    if (isVisible) {
      onLoadMore();
    }
  }, [loading, hasMore, onLoadMore]);

  return (
    <>
      {children}
      {hasMore && (
        <div ref={loadingRef} className={loaderClassName}>
          {loader || <LoadingSpinner />}
        </div>
      )}
    </>
  );
};

export default InfiniteScroll;

